// Phase 0088 — Scheduler admin page.
// Shows all 7 scheduler jobs, last run status, last duration, success/failure
// counts. Each job has Run-now + Toggle buttons + a Run log drawer.

import { useState } from 'react'
import {
  App, Badge, Button, Card, Drawer, Empty, Space, Table, Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { schedulerApi, type SchedulerJob, type SchedulerRunLog } from '@/features/hr/schedulerApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text, Paragraph } = Typography

const STATUS_COLOR: Record<string, string> = {
  SUCCESS: 'green', FAILED: 'red', RUNNING: 'blue', SKIPPED: 'default',
}

export default function SchedulerAdminPage() {
  const { message: msg } = App.useApp()
  const [drawerJob, setDrawerJob] = useState<string | null>(null)

  const { data: jobs = [], isLoading, refetch } = useQuery({
    queryKey: ['scheduler-jobs'],
    queryFn: () => schedulerApi.list(),
    refetchInterval: 15000,
  })

  const triggerMut = useMutation({
    mutationFn: (code: string) => schedulerApi.trigger(code),
    onSuccess: (data: unknown, code) => {
      const d = data as { status?: string; duration_ms?: number }
      msg.success(`${code}: ${d.status ?? 'OK'} in ${d.duration_ms ?? 0}ms`)
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const toggleMut = useMutation({
    mutationFn: (code: string) => schedulerApi.toggle(code),
    onSuccess: (_: unknown, code) => { msg.success(`${code} toggled`); refetch() },
    onError: (e: Error) => msg.error(e.message),
  })

  const columns: ColumnsType<SchedulerJob> = [
    { title: 'Code', dataIndex: 'job_code', width: 280,
      render: (c: string) => <Text code style={{ fontSize: 11 }}>{c}</Text> },
    { title: 'Job', dataIndex: 'job_name' },
    { title: 'Schedule', key: 'sched', width: 130,
      render: (_, r) => r.cron_expression
        ? <Text code style={{ fontSize: 11 }}>{r.cron_expression}</Text>
        : (r.interval_seconds ? `${r.interval_seconds}s interval` : '—') },
    { title: 'Enabled', dataIndex: 'enabled', width: 80,
      render: (b: boolean) => b
        ? <Tag color="green">ON</Tag> : <Tag>OFF</Tag> },
    { title: 'Last status', dataIndex: 'last_run_status', width: 110,
      render: (s: string | null) => s
        ? <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag> : '—' },
    { title: 'Last run', dataIndex: 'last_run_at', width: 160,
      render: (d: string | null) => d ? dayjs(d).format('MMM D HH:mm') : '—' },
    { title: 'Duration', dataIndex: 'last_run_duration_ms', width: 90,
      render: (n: number | null) => n != null ? `${n}ms` : '—' },
    { title: '✓ / ✗', key: 'counts', width: 100,
      render: (_, r) => (
        <Space size={4}>
          <Badge count={r.success_count} showZero color="#52c41a" />
          <Badge count={r.failure_count} showZero color="#cf1322" />
        </Space>
      ) },
    {
      title: 'Action', key: 'action', width: 250, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="primary"
                  onClick={() => triggerMut.mutate(r.job_code)}
                  loading={triggerMut.isPending}>
            Run now
          </Button>
          <Button size="small" onClick={() => setDrawerJob(r.job_code)}>
            Runs
          </Button>
          <Button size="small" onClick={() => toggleMut.mutate(r.job_code)}>
            {r.enabled ? 'Disable' : 'Enable'}
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Scheduler Admin"
        subtitle="Automated HR jobs — task generation, escalation sweep, scores, attendance derivation, certification expiry"
        extra={<Button onClick={() => refetch()}>Refresh</Button>}
      />
      <Table<SchedulerJob>
        rowKey="id"
        columns={columns}
        dataSource={jobs}
        loading={isLoading}
        pagination={false}
        size="middle"
        scroll={{ x: 1300 }}
      />
      <RunsDrawer
        open={!!drawerJob}
        jobCode={drawerJob}
        onClose={() => setDrawerJob(null)}
      />
    </>
  )
}

function RunsDrawer({ open, jobCode, onClose }:
  { open: boolean; jobCode: string | null; onClose: () => void }) {
  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['scheduler-runs', jobCode, open],
    queryFn: () => schedulerApi.runs(jobCode!, 30),
    enabled: !!jobCode && open,
  })
  return (
    <Drawer
      title={`Run history — ${jobCode || ''}`}
      open={open}
      onClose={onClose}
      width={680}
    >
      {isLoading ? <Text>Loading…</Text> :
        runs.length === 0 ? <Empty /> : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {runs.map((r: SchedulerRunLog) => (
              <Card key={r.id} size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Tag color={STATUS_COLOR[r.status] ?? 'default'}>{r.status}</Tag>
                    <Tag>{r.triggered_by}</Tag>
                    <Text type="secondary">
                      {r.started_at ? dayjs(r.started_at).format('MMM D HH:mm:ss') : ''}
                    </Text>
                    <Text type="secondary">{r.duration_ms}ms</Text>
                  </Space>
                  {r.error_text && (
                    <Paragraph type="danger" style={{ marginBottom: 0 }}>
                      {r.error_text}
                    </Paragraph>
                  )}
                  {r.result_json && (
                    <Text code style={{ fontSize: 11 }}>
                      {JSON.stringify(r.result_json).substring(0, 300)}
                      {JSON.stringify(r.result_json).length > 300 ? '…' : ''}
                    </Text>
                  )}
                </Space>
              </Card>
            ))}
          </Space>
        )}
    </Drawer>
  )
}
